import type { DtcCodeEntity } from '@packages/domain/dtc-codes';
import type { TechlineAssetEntity } from '@packages/domain/techline/assets';
import type { TechlineCaseEntity } from '@packages/domain/techline/cases';
import type { EditAssetProp } from '@web/components/attachment/AssetEditorModal';

import { getLogger } from '@packages/core/logging';
import { colors } from '@packages/core/styles';
import { AssetType } from '@packages/domain/techline/assets';
import { Icon, Label, TextareaHelper, Typography } from '@packages/ui/shared';
import AssetEditorModal from '@web/components/attachment/AssetEditorModal';
import { AssetViewerModal, type IAsset } from '@web/components/attachment/AssetViewerModal';
import { downloadFile } from '@web/utils';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import { ActivityIndicator, StyleSheet } from 'react-native';
import { toast } from 'react-toastify';

import { AttachmentList } from '../../../../../attachment-list/attachment-list';
import { ManageDtcCodes } from './manage-dtc-codes';

const logger = getLogger('techline');

const MAX_CHARACTER_LENGTH = 300;

/** Props for the TechnicalDescriptionStepContent component. */
export type TechnicalDescriptionStepContentProps = {
    /** The asset data for the current case. */
    assetData: TechlineAssetEntity[];

    /** The case details for the current draft case. */
    caseDetails: TechlineCaseEntity;

    /** Field-level errors computed by the validation hook. */
    errors: Record<string, string>;

    /** Whether assets are currently being uploaded. Local UI concern; defaults to false. */
    isUploadingAssets?: boolean;

    // FIXME: Should be `Partial<TechlineCaseEntity>` but ConcernDetailsCheckboxes passes `any` (TECHSHARE-5977)
    //        - Luke C, 03/15/2026
    /** Callback when case details change (e.g., technician description). */
    onCaseDetailsChange: (details: Record<string, unknown>) => void;

    /** Callback to delete an asset by ID. */
    onDeleteAsset: (assetId: string) => Promise<void>;

    /** Callback to download a file by URL and filename. Uses fetch+Blob fallback when omitted. */
    onDownloadAsset?: (src: string, fileName: string) => Promise<void>;

    /** Callback to display an error message banner in the parent view. */
    onError?: (message: string) => void;

    /** Callback to refresh the asset list. */
    onRefreshAssets: () => Promise<void>;

    /** Callback to display a success message banner in the parent view. */
    onSuccess?: (message: string) => void;

    /** Callback when a field is touched. */
    onTouched: (field: string) => void;

    /** Callback to update an existing asset (rename, edit, replace). */
    onUpdateAsset: (assetId: string, update: AssetUpdatePayload) => Promise<void>;

    /** Callback to upload a new asset file. */
    onUploadAsset: (file: File) => Promise<void>;

    /** Record of which fields have been touched by the user. */
    touched: Record<string, boolean>;

    /** Names of files currently uploading (for progress display). Local UI concern; defaults to []. */
    uploadingFiles?: string[];

    /** DTC code data for the case. */
    dtcCodeData: DtcCodeEntity[];

    /** Whether DTC codes are currently being loaded. */
    isDtcLoading: { [dtcCode: string]: boolean };

    /** Whether the primary DTC code is currently being updated. */
    isPrimaryDtcUpdating: boolean;

    /** Error message related to DTC code operations. */
    dtcErrorMessage: string;

    /** Callback to add a DTC code. */
    onAddDtc: (dtc: DtcCodeEntity[]) => Promise<void>;

    /** Callback to delete a DTC code. */
    onDeleteDtc: (dtc: DtcCodeEntity) => Promise<void>;

    /** Callback to change the primary DTC code. */
    onPrimaryChange: (dtc: DtcCodeEntity) => Promise<void>;

    /** Callback to toggle freeze frame data for a DTC code. */
    onToggleFreezeFrame: (dtc: DtcCodeEntity) => Promise<void>;
};

/** Payload for updating an existing asset's metadata or content. */
type AssetUpdatePayload = {
    /** A replacement file blob, if re-uploading. */
    blob?: File;

    /** A new display name for the asset. */
    name?: string;

    /** A new note/description for the asset. */
    note?: string;
};

/** A media entry with a guaranteed assetImageTypeId, for use by AssetEditorModal. */
type DefinedMedia = {
    /** The asset image type identifier. */
    assetImageTypeId: string;

    /** The media URL. */
    url: string;
};

/** A media entry narrowed to have a defined assetImageTypeId. */
type MediaWithDefinedTypeId = TechlineAssetEntity['media'][number] & DefinedMedia;

/** State for the asset viewer modal. */
type ViewerState = {
    /** The list of viewable attachments. */
    attachments: IAsset[];

    /** The index of the attachment to display first. */
    initialIndex: number;

    /** Whether the viewer modal is visible. */
    visible: boolean;
};

/**
 * Step content for the "Technical Description" step of the draft case stepper.
 * Displays the technician description textarea and file attachments.
 * All asset mutations and fetching are delegated to the parent via callback props.
 *
 * @param props - {@link TechnicalDescriptionStepContentProps}
 * @returns the technical description step form content
 */
export function TechnicalDescriptionStepContent({
    assetData,
    caseDetails,
    dtcCodeData,
    dtcErrorMessage,
    errors,
    isDtcLoading,
    isPrimaryDtcUpdating,
    isUploadingAssets = false,
    onAddDtc,
    onCaseDetailsChange,
    onDeleteAsset,
    onDeleteDtc,
    onDownloadAsset,
    onError,
    onPrimaryChange,
    onRefreshAssets,
    onSuccess,
    onToggleFreezeFrame,
    onTouched,
    onUpdateAsset,
    onUploadAsset,
    touched,
    uploadingFiles = [],
}: TechnicalDescriptionStepContentProps) {
    // Hooks
    const { t } = useTranslation();
    const classes = useStyles();

    // State
    const [techDescription, setTechDescription] = useState({
        technicianDescription: caseDetails?.technicianDescription || '',
    });

    const [viewerState, setViewerState] = useState<ViewerState>({
        visible: false,
        attachments: [],
        initialIndex: 0,
    });
    const [isAssetModalVisible, setIsAssetModalVisible] = useState(false);
    const [assetIndex, setAssetIndex] = useState(0);

    // Forwarded callback aliases
    const handleCaseDetailsChange = onCaseDetailsChange;

    // Derived state

    // Filter to complete image assets because only complete images are editable and shown
    // in the asset editor modal
    const editorAssets = useMemo(
        () =>
            assetData
                .filter((asset) => asset.status === 'complete' && asset.assetType === AssetType.Image)
                .map((asset) => mapAssetToEditAssetProp(asset)),
        [assetData]
    );

    const uploadingFilesLabel = useMemo(
        () => `${uploadingFiles.length} file${uploadingFiles.length !== 1 ? 's' : ''} uploading...`,
        [uploadingFiles.length]
    );

    // Handlers

    /**
     * Handles blur on a form field by marking it as touched, validating, and notifying parent.
     *
     * @param field - the field name that was blurred
     */
    const handleTechDescriptionFieldBlur = useCallback(
        (field: keyof typeof techDescription) => {
            const trimmedValue = techDescription[field].trim();
            setTechDescription((prev) => ({ ...prev, [field]: trimmedValue }));
            onTouched(field);
            handleCaseDetailsChange({ [field]: trimmedValue });
        },
        [onTouched, handleCaseDetailsChange, techDescription]
    );

    /**
     * Handles text changes in the technician description textarea.
     *
     * @param text - the new text value
     */
    const handleTechDescriptionTextChange = useCallback((text: string) => {
        setTechDescription((prev) => ({ ...prev, technicianDescription: text }));
    }, []);

    /**
     * Handles the delete button click for an asset in the attachment component.
     *
     * @param assetId - the ID of the asset to delete
     */
    const handleDeleteAssetButtonClick = useCallback(
        async (assetId: string) => {
            try {
                await onDeleteAsset(assetId);
                onSuccess?.('Asset deleted successfully!');
                setTimeout(() => onSuccess?.(''), 10000);
            } catch (error) {
                logger.error(`Failed to delete asset [${assetId}]`, { assetId, error });
                onError?.('Failed to delete asset. Please try again.');
                setTimeout(() => onError?.(''), 10000);
            }
        },
        [onDeleteAsset, onSuccess, onError]
    );

    /**
     * Handles the download action for a file attachment by looking up the asset
     * by ID, finding its original media URL, and triggering a download.
     *
     * @param assetId - the ID of the asset to download
     */
    const handleDownloadButtonClick = useCallback(
        async (assetId: string) => {
            const asset = assetData.find((a) => a.id === assetId);
            if (!asset) {
                toast.error('Asset not found. Please try again later.');
                return;
            }

            if (!asset.downloadUrl) {
                toast.error('Download URL not available. Please try again later.');
                return;
            }

            try {
                const doDownload = onDownloadAsset ?? downloadFile;
                await doDownload(asset.downloadUrl, asset.name);
            } catch {
                toast.error('Failed to download the file. Please try again.');
            }
        },
        [assetData, onDownloadAsset]
    );

    /**
     * Handles the update action for an asset from the editor modal.
     *
     * @param assetId - the ID of the asset to update
     * @param update - the update payload (name, note, blob)
     */
    const handleUpdateAssetButtonClick = useCallback(
        async (assetId: string, update: AssetUpdatePayload) => {
            try {
                await onUpdateAsset(assetId, update);
                await onRefreshAssets();
                onSuccess?.('Asset updated successfully!');
                setTimeout(() => onSuccess?.(''), 3000);
            } catch {
                onError?.('Failed to update asset. Please try again.');
                setTimeout(() => onError?.(''), 3000);
            }
        },
        [onUpdateAsset, onRefreshAssets, onSuccess, onError]
    );

    /**
     * Opens the asset viewer modal for the given asset by finding its index
     * in the complete-assets list and building the viewer's IAsset array.
     *
     * @param assetId - the ID of the asset to view
     */
    const handleViewAttachment = useCallback(
        (assetId: string) => {
            // Build the viewer assets from complete assets only
            const completeAssets = assetData.filter((a) => a.status === 'complete');
            const viewerAssets = completeAssets.map((asset) => mapAssetToViewerAsset(asset));
            const index = viewerAssets.findIndex((a) => a.id === assetId);

            setViewerState({ visible: true, attachments: viewerAssets, initialIndex: Math.max(index, 0) });
        },
        [assetData]
    );

    /**
     * Opens the asset editor modal for the given asset by finding its index
     * in the editorAssets list.
     *
     * @param assetId - the ID of the asset to edit
     */
    const handleEditAttachment = useCallback(
        (assetId: string) => {
            const index = editorAssets.findIndex((a) => a.assetId === assetId);
            setAssetIndex(Math.max(index, 0));
            setIsAssetModalVisible(true);
        },
        [editorAssets]
    );

    /** Closes the asset viewer modal and resets its state. */
    const handleViewerClose = useCallback(() => {
        setViewerState({ visible: false, attachments: [], initialIndex: 0 });
    }, []);

    /** Closes the asset editor modal. */
    const handleEditorClose = useCallback(() => {
        setIsAssetModalVisible(false);
    }, []);

    /** Downloads an asset by URL and filename (used by both the viewer and editor modals). */
    const handleAssetDownload = useCallback(
        async (imageSrc: string, fileName: string) => {
            const doDownload = onDownloadAsset ?? downloadFile;
            await doDownload(imageSrc, fileName);
        },
        [onDownloadAsset]
    );

    /**
     * Handles deleting an asset from the editor modal, then refreshes the asset list.
     *
     * @param id - the ID of the asset to delete
     */
    const handleEditorDeleteAsset = useCallback(
        async (id: string) => {
            await handleDeleteAssetButtonClick(id);
            await onRefreshAssets();
        },
        [handleDeleteAssetButtonClick, onRefreshAssets]
    );

    /**
     * Handles file uploads from the AttachmentList dropzone by uploading each file individually.
     *
     * @param files - the files selected or dropped by the user
     */
    const handleUploadFiles = useCallback(
        (files: File[]) => {
            files.forEach((file) => {
                onUploadAsset(file);
            });
        },
        [onUploadAsset]
    );

    /** Handles blur on the technician description field specifically. */
    const handleTechDescriptionBlur = useCallback(() => {
        handleTechDescriptionFieldBlur('technicianDescription');
    }, [handleTechDescriptionFieldBlur]);

    // Effects

    /**
     * Syncs local technician description state when caseDetails prop updates.
     */
    useEffect(() => {
        setTechDescription({ technicianDescription: caseDetails?.technicianDescription || '' });
    }, [caseDetails]);

    return (
        <div className={classes.container}>
            <div className="pb-3 pt-4">
                <Typography variant="h3">{t('techline:techDescription.header')}</Typography>
            </div>
            <div className="d-flex mt-3 flex-row">
                <div className={classes.flexOne}>
                    <TextareaHelper
                        containerStyle={textareaContainerStyle}
                        errorMessage={touched?.technicianDescription ? t(errors.technicianDescription) : ''}
                        label={t('techline:techDescription.technicianDescription.label')}
                        labelRightElement={
                            <div className={`d-flex align-items-center pl-1 ${classes.rightSideLabelContainer}`}>
                                <OverlayTrigger
                                    overlay={
                                        <Tooltip className={classes.tooltip} id="info-tool-tip">
                                            {t('techline:techDescription.technicianDescription.tooltip')}
                                        </Tooltip>
                                    }
                                    placement="bottom"
                                >
                                    <Icon color="grayFive" name="info" />
                                </OverlayTrigger>
                                <span className={classes.charCount}>{`${
                                    techDescription.technicianDescription.trim().length
                                }/${MAX_CHARACTER_LENGTH}`}</span>
                            </div>
                        }
                        labelStyle={labelStyle}
                        maxLength={MAX_CHARACTER_LENGTH}
                        onBlur={handleTechDescriptionBlur}
                        onChangeText={handleTechDescriptionTextChange}
                        placeholder={t('techline:techDescription.technicianDescription.placeholder')}
                        required
                        textInputStyle={textInputStyle}
                        value={techDescription.technicianDescription}
                    />
                </div>
            </div>

            <div className="d-flex flex-column mt-3">
                <div className="mb-1">
                    <Typography>{t('techline:techDescription.dtcCodes.label')}</Typography>
                </div>
                <ManageDtcCodes
                    dtcCodeData={dtcCodeData}
                    errorMessage={dtcErrorMessage}
                    isDtcLoading={isDtcLoading}
                    isPrimaryUpdating={isPrimaryDtcUpdating}
                    onAddDtc={onAddDtc}
                    onDeleteDtc={onDeleteDtc}
                    onPrimaryChange={onPrimaryChange}
                    onToggleFreezeFrame={onToggleFreezeFrame}
                />
            </div>

            <div className="mt-5">
                <div className="d-flex align-items-center" style={attachmentsHeaderStyle}>
                    <Label style={rnStyles.attachmentsLabel}>{t('techline:techDescription.attachments.label')}</Label>
                    {isUploadingAssets && (
                        <div className="d-flex align-items-center" style={uploadingIndicatorStyle}>
                            <ActivityIndicator color={colors.blueOne} size="small" />
                            <span style={uploadingTextStyle}>{uploadingFilesLabel}</span>
                        </div>
                    )}
                </div>
                <AttachmentList
                    assetData={assetData}
                    onDelete={handleDeleteAssetButtonClick}
                    onDownload={handleDownloadButtonClick}
                    onEdit={handleEditAttachment}
                    onUpload={handleUploadFiles}
                    onView={handleViewAttachment}
                />
            </div>

            {viewerState.visible && (
                <AssetViewerModal
                    assets={viewerState.attachments}
                    initialIndex={viewerState.initialIndex}
                    onClose={handleViewerClose}
                    onDownload={handleAssetDownload}
                />
            )}

            {isAssetModalVisible && (
                <AssetEditorModal
                    assets={editorAssets}
                    edit={isAssetModalVisible}
                    initialIndex={assetIndex}
                    onClose={handleEditorClose}
                    onDeleteAsset={handleEditorDeleteAsset}
                    onDownload={handleAssetDownload}
                    onUpdateAsset={handleUpdateAssetButtonClick}
                />
            )}
        </div>
    );
}

/**
 * Filters media entries to those with a defined assetImageTypeId and maps them to the
 * `{ assetImageTypeId, url }` shape expected by AssetEditorModal.
 *
 * @param mediaEntries - the raw media entries from the asset
 * @returns the filtered and mapped media entries
 */
function extractDefinedMedia(mediaEntries: TechlineAssetEntity['media']): DefinedMedia[] {
    return mediaEntries
        .filter((m): m is MediaWithDefinedTypeId => Boolean(m.assetImageTypeId))
        .map((m) => ({ assetImageTypeId: m.assetImageTypeId, url: m.url }));
}

/**
 * Maps a `TechlineAssetEntity` to the `EditAssetProp` shape expected by `AssetEditorModal`.
 *
 * @param asset - the asset to map
 * @returns the mapped edit asset prop
 */
function mapAssetToEditAssetProp(asset: TechlineAssetEntity): EditAssetProp {
    // Map media entries to the shape expected by AssetEditorModal
    const media = extractDefinedMedia(asset.media || []);

    return {
        assetId: asset.id,
        assetTypeId: asset.assetType,
        downloadUrl: asset.downloadUrl,
        media,
        name: asset.name,
        note: asset.note || '',
        preview: asset?.thumbnailUrl || asset.downloadUrl,
    };
}

/**
 * Maps a `TechlineAssetEntity` to the `IAsset` shape expected by `AssetViewerModal`.
 *
 * @param asset - the asset to map
 * @returns the mapped viewer asset
 */
function mapAssetToViewerAsset(asset: TechlineAssetEntity): IAsset {
    return {
        assetType: asset.assetType,
        contentType: asset.contentType,
        downloadUrl: asset.downloadUrl,
        id: asset.id,
        name: asset.name,
        thumbnailUrl: asset?.thumbnailUrl,
        uploadedAt: new Date(asset.createdAt),
    };
}

// Style constants for inline styles that cannot be expressed as Tailwind classes

const textareaContainerStyle = { height: 60, borderRadius: 4 };

const labelStyle = { fontWeight: 400 as const };

const textInputStyle = { fontSize: 15, fontWeight: 400 as const };

const attachmentsHeaderStyle = { gap: 8 };

const uploadingIndicatorStyle = { gap: 6 };

const uploadingTextStyle = { fontSize: 13, fontStyle: 'italic' as const, color: colors.textDarkSecondary };

const rnStyles = StyleSheet.create({
    attachmentsLabel: {
        fontSize: 15,
        fontWeight: 400,
    },
});

const useStyles = createUseStyles({
    container: {
        minHeight: 0,
        padding: '16px 80px',
    },
    flexOne: {
        flex: 1,
    },
    tooltip: {
        marginTop: 5,
        width: 272,
        '& .tooltip-inner': {
            backgroundColor: colors.brandSecondary,
            borderRadius: 4,
            fontSize: '13px',
            fontWeight: 400,
            lineHeight: '16px',
            maxWidth: 272,
            padding: '3px 8px',
            textAlign: 'left',
        },
    },
    rightSideLabelContainer: {
        justifyContent: 'space-between',
        width: '100%',
    },
    charCount: {
        color: colors.textDarkSecondary,
        fontSize: 13,
        fontWeight: 400,
    },
});
